import React, { createContext, useContext, useState, useEffect } from 'react';
import { detectUserLanguage, isFirstVisit } from '../services/geoLocationService';

// Create a context for language
const LanguageContext = createContext();

// Custom hook to use the language context
export const useLanguage = () => useContext(LanguageContext);

// Language provider component
export const LanguageProvider = ({ children }) => {
  // Get the stored language or detect based on browser for first visit
  const [language, setLanguage] = useState('en'); // Default to 'en' initially
  const [isLoading, setIsLoading] = useState(true);

  // Initialize language on first render
  useEffect(() => {
    // Check if this is the first visit
    if (isFirstVisit()) {
      // Detect language based on browser settings
      const detectedLang = detectUserLanguage();
      setLanguage(detectedLang);
      localStorage.setItem('language', detectedLang);
    } else {
      // Use previously stored language
      const storedLanguage = localStorage.getItem('language');
      setLanguage(storedLanguage || 'en');
    }
    // Short timeout to avoid flash of unstyled content
    setTimeout(() => setIsLoading(false), 50);
  }, []);

  // Update localStorage and document lang attribute when language changes
  useEffect(() => {
    if (!isLoading) {
      localStorage.setItem('language', language);
      document.documentElement.setAttribute('lang', language);
    }
  }, [language, isLoading]);
  
  // Toggle between languages ('en', 'hr', 'de', 'fr')
  const toggleLanguage = () => {
    setLanguage(prevLang => {
      switch(prevLang) {
        case 'en': return 'hr';
        case 'hr': return 'de';
        case 'de': return 'fr';
        case 'fr': 
        default: return 'en';
      }
    });
  };

  // Set to a specific language
  const setSpecificLanguage = (lang) => {
    if (['en', 'hr', 'de', 'fr'].includes(lang)) {
      setLanguage(lang);
    }
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, setSpecificLanguage, isLoading }}>
      {children}
    </LanguageContext.Provider>
  );
};
